// PromptGenerator/components/PromptViewer.tsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh, faEdit, faCheck, faTimes, faCopy, faImage, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import styles from '../PromptLayout.module.css';
import { parsePromptIntoCards } from '../utils/promptParsing';
import { useNotification } from '../../../context/NotificationContext';
import { PromptData } from '../hooks/useProjectData';
import { usePromptEditor } from '../hooks/usePromptEditor';

// Helper function from PromptLayout.tsx
const shouldShowGenerateImageButton = (promptTitle: string): boolean => {
  if (!promptTitle) return false;
  const titleLower = promptTitle.toLowerCase();
  return titleLower.includes('image') || titleLower.includes('character');
};

interface PromptViewerProps {
  selectedPrompt: PromptData;
  loadPrompts: () => Promise<void>;
  updatePromptsList: (updatedPrompt: PromptData) => void;
  handleRegeneratePrompt: () => void;
  handleGenerateImage: (promptData: PromptData) => Promise<void>;
}

const PromptViewer: React.FC<PromptViewerProps> = ({
  selectedPrompt, loadPrompts, updatePromptsList, handleRegeneratePrompt, handleGenerateImage
}) => {
  const { t } = useTranslation('promptGenerator');
  const { showNotification } = useNotification();
  
  // Destructure editing state and handlers
  // Note: Only edit handlers related to content and idea are pulled here.
  const {
    editingPrompt, editedPrompt, setEditedPrompt, handlePromptEdit, handlePromptSave, handlePromptCancel,
    editingOriginalIdea, editedOriginalIdea, setEditedOriginalIdea, handleOriginalIdeaEdit, handleOriginalIdeaSave, handleOriginalIdeaCancel
  } = usePromptEditor(null, () => {}, selectedPrompt, () => {}, []);

  const parsedResult = useMemo(() => {
    return parsePromptIntoCards(selectedPrompt.generatedPrompt);
  }, [selectedPrompt.generatedPrompt]);

  const { prompts: contentPrompts } = parsedResult;
  const hasMultiplePrompts = contentPrompts.length > 1;

  const handlePromptSaveWrapper = () => handlePromptSave(loadPrompts, updatePromptsList);
  const handleOriginalIdeaSaveWrapper = () => handleOriginalIdeaSave(updatePromptsList);
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className={styles.promptViewer}>
      <div className={styles.promptHeader}>
        <div className={styles.promptTitleSection}>
          <h1 className={styles.promptTitle}>{selectedPrompt.title || t('promptGenerator.promptLayout.noPrompts')}</h1>
          <div className={styles.promptHeaderActions}>
            <button
              className={styles.regenerateButton}
              onClick={handleRegeneratePrompt}
              title={t('promptGenerator.promptLayout.regenerate')}
            >
              <FontAwesomeIcon icon={faRefresh} />
              {t('promptGenerator.promptLayout.regenerate')}
            </button>
          </div>
        </div>
        <div className={styles.promptMetadata}>
          <small>{t('promptGenerator.promptViewer.created')}: {formatDate(selectedPrompt.createdAt)}</small>
          {selectedPrompt.updatedAt !== selectedPrompt.createdAt && (
            <small>{t('promptGenerator.promptViewer.updated')}: {formatDate(selectedPrompt.updatedAt)}</small>
          )}
        </div>
      </div>
      
      <div className={styles.promptContent}>
        {/* Original Idea Section */}
        <div className={styles.promptSection}>
          <div className={styles.promptSectionHeader}>
            <h3>{t('promptGenerator.promptViewer.originalIdea')}</h3>
            <button
              className={styles.editPromptBtn}
              onClick={editingOriginalIdea ? handleOriginalIdeaSaveWrapper : handleOriginalIdeaEdit}
              title={editingOriginalIdea ? t('promptGenerator.promptViewer.saveOriginalIdea') : t('promptGenerator.promptViewer.editOriginalIdea')}
            >
              <FontAwesomeIcon icon={editingOriginalIdea ? faCheck : faEdit} />
            </button>
          </div>
          {editingOriginalIdea ? (
            <div className={styles.editingContainer}>
              <textarea
                className={styles.ideaEditor}
                value={editedOriginalIdea}
                onChange={(e) => setEditedOriginalIdea(e.target.value)}
                rows={3}
              />
              <div className={styles.editingActions}>
                <button className={styles.saveButton} onClick={handleOriginalIdeaSaveWrapper} title={t('promptGenerator.promptViewer.saveChanges')}><FontAwesomeIcon icon={faCheck} /></button>
                <button className={styles.cancelButton} onClick={handleOriginalIdeaCancel} title={t('promptGenerator.promptLayout.cancel')}><FontAwesomeIcon icon={faTimes} /></button>
              </div>
            </div>
          ) : (
            <p>{selectedPrompt.userSelections?.userIdea || t('promptGenerator.promptViewer.noIdeaRecorded')}</p>
          )}
        </div>
        
        {/* Generated Prompt Section */}
        <div className={styles.promptSection}>
          <div className={styles.promptSectionHeader}>
            <h3>{t('promptGenerator.promptLayout.generatedPrompt')}</h3>
            <button
              className={styles.copyButton}
              onClick={() => {
                navigator.clipboard.writeText(selectedPrompt.generatedPrompt);
                showNotification({ title: t('promptGenerator.notifications.copied.title'), message: t('promptGenerator.notifications.copied.message'), type: 'success' });
              }}
              title={t('promptGenerator.promptViewer.copyPrompt')}
            >
              <FontAwesomeIcon icon={faCopy} />
            </button>
            <button
              className={styles.editPromptBtn}
              onClick={editingPrompt ? handlePromptSaveWrapper : handlePromptEdit}
              title={editingPrompt ? t('promptGenerator.promptViewer.saveGeneratedPrompt') : t('promptGenerator.promptViewer.editGeneratedPrompt')}
            >
              <FontAwesomeIcon icon={editingPrompt ? faCheck : faEdit} />
            </button>
            
            {shouldShowGenerateImageButton(selectedPrompt.title || '') && (
              <button
                className={styles.generateImageButton}
                onClick={() => handleGenerateImage(selectedPrompt)}
                title={t('promptGenerator.buttons.generateImage')}
              >
                <FontAwesomeIcon icon={faImage} />
                {t('promptGenerator.buttons.generateImage')}
              </button>
            )}
          </div>
          
          {editingPrompt ? (
            <div className={styles.editingContainer}>
              <textarea
                className={styles.promptEditor}
                value={editedPrompt}
                onChange={(e) => setEditedPrompt(e.target.value)}
                rows={10}
              />
              <div className={styles.editingActions}>
                <button className={styles.saveButton} onClick={handlePromptSaveWrapper} title={t('promptGenerator.promptViewer.saveChanges')}><FontAwesomeIcon icon={faCheck} /></button>
                <button className={styles.cancelButton} onClick={handlePromptCancel} title={t('promptGenerator.promptLayout.cancel')}><FontAwesomeIcon icon={faTimes} /></button>
              </div>
            </div>
          ) : (
            <>
              {!hasMultiplePrompts && (
                <div className={styles.promptText}>
                  {contentPrompts[0] || selectedPrompt.generatedPrompt}
                </div>
              )}
              
              {hasMultiplePrompts && (
                <div className={styles.productionNotesAccordion}>
                  <details className={styles.accordionDetails}>
                    <summary className={styles.accordionSummary}>
                      <span>{t('promptGenerator.promptViewer.productionNotes')} ({contentPrompts.length} {t('promptGenerator.promptViewer.sections')})</span>
                      <FontAwesomeIcon icon={faChevronDown} className={styles.accordionIcon} />
                    </summary>
                    <div className={styles.accordionContent}>
                      {contentPrompts.map((prompt, index) => (
                        <div key={index} className={styles.productionNote}>
                          <div className={styles.productionNoteHeader}>
                            <span>{t('promptGenerator.results.promptNumber')} {index + 1}</span>
                            <button
                              className={styles.copyButton}
                              onClick={() => {
                                navigator.clipboard.writeText(prompt);
                                showNotification({ title: t('promptGenerator.notifications.copied.title'), message: t('promptGenerator.promptViewer.sectionCopied', { section: index + 1 }), type: 'success' });
                              }}
                              title={t('promptGenerator.promptViewer.copySection')}
                            >
                              <FontAwesomeIcon icon={faCopy} />
                            </button>
                          </div>
                          <div className={styles.productionNoteText}>{prompt}</div>
                        </div>
                      ))}
                    </div>
                  </details>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromptViewer;